import { Injectable, inject } from '@angular/core';

import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { isNil } from 'lodash-es';

import { StoryContentConstants, StoryContentTrackingData, TrackingActionEvent, TrackingEventDetails } from './story-content.model';

@Injectable({ providedIn: 'root' })
export class StoryContentTrackingService {
    private trackingService = inject(TrackingService);

    trackStoryAndSubstory(isStory: boolean, isNext: boolean, storyTitle: string): void {
        const eventDetails = this.getEventDetails(isStory, isNext);
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Click,
            position: storyTitle,
            location: NOT_APPLICABLE,
            eventDetails,
        };
        this.trackStoryContent(trackingData);
    }

    trackStoryAndSubstoryOnLoad(isStory: boolean, isNext: boolean, storyTitle: string, isPopupLoad: boolean): void {
        const eventDetails = this.getEventDetails(isStory, isNext);
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Load,
            position: storyTitle,
            location: NOT_APPLICABLE,
            eventDetails: isPopupLoad ? TrackingEventDetails.Story : eventDetails,
        };
        this.trackStoryContent(trackingData);
    }

    trackCtaButtonClick(storyTitle: string): void {
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Click,
            position: storyTitle,
            location: TrackingEventDetails.Substory,
            eventDetails: TrackingEventDetails.SeeMore,
        };
        this.trackStoryContent(trackingData);
    }

    trackPopupClose(isCtaPopup: boolean, storyTitle: string): void {
        const trackingData: StoryContentTrackingData = {
            action: isCtaPopup ? TrackingActionEvent.Click : TrackingActionEvent.Exit,
            position: storyTitle,
            location: isCtaPopup ? TrackingEventDetails.Substory : NOT_APPLICABLE,
            eventDetails: isCtaPopup ? TrackingEventDetails.Done : TrackingEventDetails.DismissStory,
        };
        this.trackStoryContent(trackingData);
    }

    trackStoryClick(storyTitle: string, index: number): void {
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Click,
            position: storyTitle,
            location: NOT_APPLICABLE,
            eventDetails: TrackingEventDetails.StoryTile,
            index,
        };
        this.trackStoryContent(trackingData);
    }

    trackStoryDismissModule(): void {
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Exit,
            position: NOT_APPLICABLE,
            location: NOT_APPLICABLE,
            eventDetails: TrackingEventDetails.DismissOnboardingStoriesModule,
        };
        this.trackStoryContent(trackingData);
    }

    trackOptInClick(promoName: string, storyTitle: string): void {
        const trackingData: StoryContentTrackingData = {
            action: TrackingActionEvent.Click,
            position: promoName,
            location: storyTitle,
            eventDetails: TrackingEventDetails.OptIn,
        };
        this.trackStoryContent(trackingData);
    }

    private getEventDetails(isStory: boolean, isNext: boolean): TrackingEventDetails {
        return isStory
            ? isNext
                ? TrackingEventDetails.NextStory
                : TrackingEventDetails.PreviousStory
            : isNext
              ? TrackingEventDetails.NextSubstory
              : TrackingEventDetails.PreviousSubstory;
    }

    private trackStoryContent(data: StoryContentTrackingData): void {
        const trackingData: {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: StoryContentConstants;
            [trackingConstants.COMPONENT_LABEL_EVENT]: StoryContentConstants;
            [trackingConstants.COMPONENT_ACTION_EVENT]: string;
            [trackingConstants.COMPONENT_POSITION_EVENT]: string;
            [trackingConstants.COMPONENT_LOCATION_EVENT]: string;
            [trackingConstants.COMPONENT_EVENT_DETAILS]: string;
            [trackingConstants.COMPONENT_URL_CLICKED]: string;
            [trackingConstants.COMPONENT_CONTENT_POSITION]?: string;
        } = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: StoryContentConstants.TrackingCategoryEvent,
            [trackingConstants.COMPONENT_LABEL_EVENT]: StoryContentConstants.TrackingLabelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: data.action,
            [trackingConstants.COMPONENT_POSITION_EVENT]: data.position,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: data.location,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: data.eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };

        if (!isNil(data.index)) {
            trackingData[trackingConstants.COMPONENT_CONTENT_POSITION] = data.index.toString();
        }

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }
}
